import Store from '../wrappers/Store';
import { I18n } from '../wrappers/I18n';
/**
 * The receipt logo (PrintSystem.md § Receipt logo): the venue's print-ready logo (`logo_print`, a
 * base64 1-bit PNG of at most 384 × 160 dots) at the top of every receipt, when the market allows
 * it and the venue switched it on.
 *
 * On the way to paper it is two text lines, always the receipt's first — an empty line, then
 * `<logo>` + base64 + `</logo>` — and in HTML one block between `<!--receipt-logo-->` markers that
 * opens with an empty line too (driver printers cut the top of a logo printed flush at the top of
 * the page). The terminal that drives the printer has the last word (PrintService.sendToPrinter):
 * a print sent to another terminal never carries the logo, and a device whose driver cannot print
 * it drops it. Every logo line is taken out on the way with the empty line above it, wherever it
 * sits — the drivers would print one anywhere — and at most one comes back, at the top.
 */
/**
 * A print copy is a base64 PNG, and every PNG's base64 starts with its file signature. Requiring it
 * means no text a person typed can ever be taken for the logo — not even one shaped like the line.
 */
const PNG = /^iVBORw0KGgo[A-Za-z0-9+/]*={0,2}$/;
const LOGO_LINE = /^<logo>(iVBORw0KGgo[A-Za-z0-9+/]*={0,2})<\/logo>$/;
const TEXT_MARKER = '<logo>iVBORw0KGgo';
const HTML_MARKER = '<!--receipt-logo-->';
const HTML_BLOCKS = /<!--receipt-logo-->[\s\S]*?<!--\/receipt-logo-->/g;
const EMPTY_HTML_BLOCK = '<!--receipt-logo--><!--/receipt-logo-->';
/** One empty receipt line in HTML: the height PrintableHtml gives an empty line. */
const EMPTY_LINE_HTML = '<div style="min-height: 10px;"></div>';
/** The roll the HTML width is measured against: 72 mm of print on 80 mm paper, at 203 dpi. */
const ROLL_DOTS = 576;
const NORMAL_LINE = { width: 'normal', height: 'normal', bold: false };
/**
 * The logo a receipt of this venue starts with, or null: the market allows it
 * (`order.allowReceiptLogo`), the venue switched Logo na računu on, and it has a print copy.
 */
export function receiptLogoFor(venue) {
    const logo = venue?.logo_print;
    if (!logo || !venue?.settings?.logo_on_receipt)
        return null;
    if (!I18n.c('order.allowReceiptLogo'))
        return null;
    return PNG.test(logo) ? logo : null;
}
/** The text line a receipt logo travels as, or null for anything that is not a base64 PNG. */
export function receiptLogoLine(logo) {
    return PNG.test(logo) ? `<logo>${logo}</logo>` : null;
}
/**
 * The HTML block of a receipt logo — an empty line, then the logo sized to its share of the 80 mm
 * roll — or null when the payload is not a readable base64 PNG (content is never interpreted as
 * markup).
 */
export function receiptLogoHtml(logo) {
    const width = PNG.test(logo) ? pngWidth(logo) : null;
    if (!width)
        return null;
    const share = Math.min(100, (width / ROLL_DOTS) * 100).toFixed(2);
    return '<!--receipt-logo-->'
        + EMPTY_LINE_HTML
        + '<div style="display: flex; justify-content: center; margin: 0 0 1em;">'
        + `<img src="data:image/png;base64,${logo}" alt="" style="width: ${share}%; image-rendering: pixelated;">`
        + '</div>'
        + '<!--/receipt-logo-->';
}
/**
 * Take every receipt logo out of a print job — its lines and the empty line above each, with their
 * highlight entries, and its HTML blocks (the markers stay, empty). Returns the first logo found,
 * and the job untouched (the same object) when it carries none.
 */
export function withoutReceiptLogo(data) {
    // Every print without a logo — every print of a venue that has not switched it on — stops
    // here: two substring checks, and the job goes on as the very same object, whatever shape it
    // has (a job from an older terminal included).
    const text = typeof data.text === 'string' ? data.text : '';
    const html = typeof data.html === 'string' ? data.html : '';
    if (!text.includes(TEXT_MARKER) && !html.includes(HTML_MARKER))
        return { data, logo: null };
    return takeReceiptLogo(data, text, html);
}
function takeReceiptLogo(data, text, htmlIn) {
    const lines = text.split('\n');
    const html = htmlIn.replace(HTML_BLOCKS, EMPTY_HTML_BLOCK);
    let logo = null;
    const isLogo = lines.map(line => {
        const match = line.match(LOGO_LINE);
        if (match && logo === null)
            logo = match[1];
        return !!match;
    });
    // a logo line goes with the empty line printed above it
    const keep = lines.map((line, index) => !isLogo[index] && !(line === '' && isLogo[index + 1]));
    if (keep.every(Boolean) && html === htmlIn)
        return { data, logo: null };
    return {
        data: {
            ...data,
            text: lines.filter((_, index) => keep[index]).join('\n'),
            highlight: (data.highlight ?? []).filter((_, index) => keep[index] ?? true),
            html,
        },
        logo,
    };
}
/** Put a receipt logo back at the top of a job that `withoutReceiptLogo` emptied, after one empty line. */
export function withReceiptLogo(data, logo) {
    const line = receiptLogoLine(logo);
    if (!line)
        return data;
    const block = receiptLogoHtml(logo);
    return {
        ...data,
        text: `\n${line}\n${data.text}`,
        highlight: [NORMAL_LINE, NORMAL_LINE, ...data.highlight],
        // the first emptied block is the receipt's own: the logo leads the receipt
        html: block ? data.html.replace(EMPTY_HTML_BLOCK, block) : data.html,
    };
}
/**
 * The logo this terminal adds to a job another terminal sent without it: only what its own venue
 * data allows — the market, Logo na računu on, a print copy — so both ends answer to the setting.
 */
export function ownReceiptLogo() {
    return receiptLogoFor(Store.get('tenant')?.venue);
}
/** Width in pixels from a base64 PNG's IHDR chunk, or null when it is not a PNG. */
function pngWidth(base64) {
    try {
        const head = atob(base64.slice(0, 32));
        if (head.slice(1, 4) !== 'PNG')
            return null;
        const width = ((head.charCodeAt(16) << 24) | (head.charCodeAt(17) << 16) | (head.charCodeAt(18) << 8) | head.charCodeAt(19)) >>> 0;
        return width > 0 ? width : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=receiptLogo.js.map